import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Card,
  Loader,
  Text,
  Pagination,
  Flex,
  SegmentedControl,
  Stack,
  Modal,
  UnstyledButton,
  Badge,
  NumberFormatter,
  TextInput,
} from "@mantine/core";
import {
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import entityIcon from "@util/entityIcon";
import { AnimatedItem } from "@components/shared/PaginatedList";
import { UserInteractionMiniDetail } from "@components/UserInteraction";
import { EmptyMessage } from "@components/shared";
import { getBadgeConfig } from "./helpers";
import formatVehicle from "@util/formatVehicle";

import hogLogo from "@assets/images/hog-logo-color.png";

export default function UserInteractionTable({
  reqData,
  isRideChallenge = false,
  useCheckins = false,
  useKeywords = false,
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(-1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search_value: "",
    context: "registration",
  });

  const CancelToken = axios.CancelToken;
  const cancelRef = useRef(null);

  const table = useReactTable({
    manualPagination: true,
    pageCount: pageCount,
    data: items,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  useEffect(() => {
    fetchData();
  }, [JSON.stringify(table.getState().pagination), JSON.stringify(filters)]);

  function fetchData() {
    const req = {
      ...reqData,
      ...filters,
      page_size: table.getState().pagination.pageSize,
      page:
        table.getState().pagination.pageIndex === -1
          ? 0
          : table.getState().pagination.pageIndex,
    };

    const cancel = cancelRef.current;
    if (cancel) cancel();

    setLoading(true);

    axios
      .post("/retrieve-user-interactions/", req, {
        cancelToken: new CancelToken(function executor(c) {
          cancelRef.current = c;
        }),
      })
      .then(({ data }) => {
        setLoading(false);
        setItems(
          data.response[0].data.map((m) => ({
            ...m,
          }))
        );
        setPageCount(data.response[0].page_count);
        if (data.response[0].total !== undefined) {
          setTotal(data.response[0].total);
        } else {
          setTotal(data.response[0].data.length);
        }
      })
      .catch((err) => {
        setLoading(false);
        setItems([]);
        setTotal(0);
      });
  }

  const contextFilters = [
    { label: t("common:registration_singular"), value: "registration" },
    ...(useKeywords
      ? [{ label: t("mixer:keyword_singular"), value: "keyword" }]
      : []),
    ...(useCheckins
      ? [{ label: t("mixer:check_in_singular"), value: "check_in" }]
      : []),
    ...(isRideChallenge
      ? [
          {
            label: t("mixer:reading_singular"),
            value: "has_user_vehicle_data_point",
          },
        ]
      : []),
    { label: t("common:all"), value: "entire" },
  ];

  return (
    <div>
      <TextInput
        placeholder={t("common:search")}
        value={filters.search_value}
        onChange={(e) =>
          setFilters({ ...filters, search_value: e.target.value })
        }
        size="lg"
        mb="sm"
      />
      {contextFilters.length > 2 && (
        <Flex gap="sm" align="center" mb="sm">
          <SegmentedControl
            value={filters.context}
            onChange={(e) => setFilters({ ...filters, context: e })}
            data={contextFilters}
          />
          {loading && <Loader size="xs" variant="dots" />}
        </Flex>
      )}
      <Stack gap="sm">
        {!loading && total === 0 && (
          <EmptyMessage
            text={t("mixer:interactions_no_results_text")}
            title={t("mixer:interactions_no_results_title")}
          />
        )}
        {table.getRowModel().rows.map((item) => (
          <AnimatedItem key={item.id}>
            <InteractionItem
              item={item.original}
              reqInfo={reqData}
              isRideChallenge={isRideChallenge}
            />
          </AnimatedItem>
        ))}
      </Stack>
      <Flex gap="sm" align="center" mt="lg">
        <Pagination
          value={table.getState().pagination.pageIndex + 1}
          onChange={(e) => {
            table.setPageIndex(e - 1);
            window.scrollTo(0, 0);
          }}
          total={pageCount}
        />
        <Text size="sm">
          {total} {t("common:total")}{" "}
          {total === 1 ? t("common:item_singular") : t("common:item_plural")}
        </Text>
        {loading && <Loader size="xs" variant="dots" />}
      </Flex>
    </div>
  );
}

const InteractionItem = ({ item, reqInfo, isRideChallenge }) => {
  const [isOpen, setIsOpen] = useState(false);
  const badgeConfig = item.is_user_vehicle_data_point
    ? getBadgeConfig(17)
    : item.keyword_text
    ? getBadgeConfig(12)
    : item.check_in
    ? getBadgeConfig(16)
    : getBadgeConfig(item.variety);
  return (
    <Card>
      <Modal
        opened={isOpen}
        onClose={() => setIsOpen(false)}
        title={badgeConfig?.label.toLocaleUpperCase() || "Interaction Detail"}
        size="lg"
      >
        <UserInteractionMiniDetail interactionId={item.id} />
      </Modal>
      <Stack gap={1}>
        <Flex gap="xs" align="center">
          <UnstyledButton onClick={() => setIsOpen(true)}>
            <Text fw={600}>{new Date(item.created_at).toLocaleString()}</Text>
          </UnstyledButton>
          <Badge variant="light" size="xs" color={badgeConfig.color}>
            {badgeConfig.label}
          </Badge>
        </Flex>
        <Flex gap="xs" align="center">
          <div>{entityIcon.user()}</div>
          <Text size="sm">
            {item.full_name}
            {item.user_created && <span> (new user)</span>}
          </Text>
        </Flex>
        <Flex gap="xs" align="center">
          <div>{entityIcon.email()}</div>
          <Text size="sm">{item.email}</Text>
        </Flex>
        <Flex gap="xs" align="center">
          <div>{entityIcon.phone()}</div>
          <Text size="sm">{item.phone}</Text>
        </Flex>
        {item.location_name && (
          <Flex gap="xs" align="center">
            <div>{entityIcon.location()}</div>
            <Flex gap="xs" align="center">
              <Text size="sm">{item.location_name}</Text>
              {item.present_at_location && (
                <Badge variant="light" size="xs" color="green">
                  Present at Location
                </Badge>
              )}
            </Flex>
          </Flex>
        )}
        {item.organization_name && (
          <Flex gap="xs" align="center">
            <div>{entityIcon.organization()}</div>
            <Text size="sm">{item.organization_name}</Text>
          </Flex>
        )}
        {!reqInfo?.campaign_effort_id && item.campaign_effort_name && (
          <Flex gap="xs" align="center">
            <div>{entityIcon.effort()}</div>
            <Text size="sm">{item.campaign_effort_name}</Text>
          </Flex>
        )}
        {item.keyword_text && (
          <Flex gap="xs" align="center">
            <div>{entityIcon.keyword()}</div>
            <Text size="sm">{item.keyword_text}</Text>
          </Flex>
        )}
        {!isRideChallenge &&
          ![0, undefined, null].includes(item.entry_count) && (
            <Flex gap="xs" align="center">
              <div>{entityIcon.entry()}</div>
              <Text size="sm">
                {item.entry_count} entr{item.entry_count === 1 ? "y" : "ies"}
              </Text>
            </Flex>
          )}
        {item.hog_member_number && (
          <Flex gap="xs" align="center">
            <div
              style={{
                background: "#000",
                padding: "0 2px",
                borderRadius: "3px",
                width: "16px",
                height: "18px",
              }}
            >
              <img
                src={hogLogo}
                alt="HOG Logo"
                style={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  objectFit: "contain",
                }}
              />
            </div>
            <Text size="sm">{item.hog_member_number}</Text>
          </Flex>
        )}
        {item.vin && (
          <Flex gap="xs" align="center">
            <div>{entityIcon.rideChallenge()}</div>
            <Text size="sm">
              {formatVehicle(item)} -{" "}
              <NumberFormatter thousandSeparator value={item.odometer_value} />{" "}
              mi.
            </Text>
          </Flex>
        )}
      </Stack>
    </Card>
  );
};

{
  /* <Card style={{ position: "relative" }}>
  <LoadingOverlay visible={loading} />
  <Table>
    <Table.Thead>
      <Table.Tr>
        <Table.Th>Time</Table.Th>
        <Table.Th>Email</Table.Th>
        <Table.Th>Location</Table.Th>
      </Table.Tr>
    </Table.Thead>
    <Table.Tbody>
      {table.getRowModel().rows.map((item) => (
        <Table.Tr key={item.id}>
          <Table.Td>
            {new Date(item.original.created_at).toLocaleString()}
          </Table.Td>
          <Table.Td>{item.original.email}</Table.Td>
          <Table.Td>{item.original.location_name}</Table.Td>
        </Table.Tr>
      ))}
    </Table.Tbody>
  </Table>
</Card> */
}
